import React, { useEffect, useRef, useState } from 'react';
import { beginPortalMfa, verifyPortalMfaOrRefresh, FRESH_CODE_NOTICE, PortalMfaSetupError } from '../../lib/portalMfa';
import { doorForAddress } from '../../lib/signInDoors';

export default function AdminAuthenticatorCard({ client, onDone, onSignOut }) {
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(true);
    const setupPromise = useRef(null);

    useEffect(() => {
        let active = true;
        // Strict Mode must not create a second enrollment during its effect replay.
        if (!setupPromise.current) {
            setupPromise.current = beginPortalMfa(client.auth, {
                // The name the Passwords app shows for a new setup follows the door; existing factors are not touched.
                friendlyName: doorForAddress(window.location).identity.authenticatorLabel,
                forceChallenge: true,
            });
        }
        setupPromise.current.then((result) => {
            if (active) { setSetup(result); setBusy(false); }
        }).catch((failure) => {
            console.error('Admin authenticator setup failed:', failure);
            if (active) {
                setError(failure instanceof PortalMfaSetupError ? failure.message
                    : 'Your authenticator could not be opened. Sign out and try again. If you lost your phone, ask your Platform Owner for recovery help.');
                setBusy(false);
            }
        });
        return () => { active = false; };
    }, [client]);

    async function verify(event) {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            const { refreshed } = await verifyPortalMfaOrRefresh(client.auth, setup, code);
            if (refreshed) {
                // The old code window ran out: a new one is already open on the same authenticator.
                setSetup({ ...refreshed, fresh: true }); setCode('');
                return;
            }
            const { data, error: identityError } = await client.rpc('bl407_identity');
            if (identityError) throw identityError;
            if (data?.mfa_satisfied !== true) throw new Error('Authenticator assurance was not confirmed.');
            await onDone();
        } catch (failure) {
            console.error('Admin authenticator verification failed:', failure);
            setError('That code could not be verified. Enter the current six-digit code from your authenticator.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={verify} className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-6 text-[var(--ink)]">
            <h1 className="mb-3 text-xl font-semibold">Admin authenticator</h1>
            <p className="mb-4 text-sm">Admins use an authenticator app every time they sign in to Central Command.</p>
            {setup?.kind === 'enroll' && (
                <>
                    <p className="mb-3 text-sm">Scan this QR code with your authenticator app, then enter its six-digit code. Keep a secure backup of your authenticator.</p>
                    {setup.qrCode && <img alt="Scan to set up your admin authenticator" src={setup.qrCode} className="mx-auto mb-3 h-48 w-48" />}
                    <p className="mb-4 break-all text-sm">Cannot scan? Enter this setup key in the app: <code>{setup.secret}</code></p>
                </>
            )}
            {busy && !setup && <p role="status">Opening your authenticator...</p>}
            {error && <p role="alert" className="mb-3 text-sm text-red-600">{error}</p>}
            {setup?.fresh && !error && <p role="status" className="mb-3 text-sm">{FRESH_CODE_NOTICE}</p>}
            {setup && (
                <>
                    <label htmlFor="admin-authenticator-code" className="block text-sm">Authenticator code</label>
                    <input id="admin-authenticator-code" type="text" inputMode="numeric" autoComplete="one-time-code"
                        pattern="[0-9]{6}" maxLength={6} required value={code}
                        onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                        className="my-3 min-h-[44px] w-full rounded border border-[var(--field-border)] bg-[var(--field-bg)] p-3 text-lg text-[var(--field-ink)]" />
                    <button type="submit" disabled={busy} className="min-h-[44px] w-full rounded bg-[#03B7C9] p-3 text-[#0B1220]">
                        {busy ? 'Checking code...' : 'Verify and open Central Command'}
                    </button>
                </>
            )}
            <button type="button" disabled={busy} onClick={onSignOut} className="mt-4 min-h-[44px] w-full text-sm">Sign out</button>
            <p className="mt-4 text-sm">Lost your authenticator? Ask your Platform Owner for identity-checked recovery. Password resets do not bypass this step.</p>
        </form>
    );
}
